"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Coins, Target, Zap, ChartBar as BarChart3, ShieldCheck, Check, X, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  icon: UserCheck,
  title: "Direct Consultant Access",
  highlight: "Zero Middlemen",
  description: "You communicate directly with me, Yassine. There are no junior account managers relaying messages or diluting strategic direction.",
  agencyDrawback: "Passed to junior staff post-pitch with account manager buffer."
}, {
  icon: Coins,
  title: "Cost Efficiency",
  highlight: "Maximum ROI",
  description: "Every pound you invest goes directly into senior SEO strategy and execution, not agency sales team commissions or lavish office overheads.",
  agencyDrawback: "High monthly retainers inflated by agency operational markup."
}, {
  icon: Target,
  title: "Bespoke Strategy",
  highlight: "100% Tailored",
  description: "I craft tailored roadmaps tailored strictly to your industry, technical stack, and business goals, refusing templated checklists.",
  agencyDrawback: "Copy-paste monthly audit templates and generic agency deliverables."
}, {
  icon: Zap,
  title: "Agile Implementation",
  highlight: "Fast Execution",
  description: "Decisions and technical fixes happen swiftly. I work seamlessly with your internal developers or implement changes directly.",
  agencyDrawback: "Slow red tape, multi-layer approval chains, and delayed tickets."
}, {
  icon: BarChart3,
  title: "Honest Reporting",
  highlight: "Transparent Impact",
  description: "Clear, plain-English performance reporting tied directly to organic revenue, lead volume, and qualified business growth.",
  agencyDrawback: "Fluffed ranking reports and superficial vanity metrics."
}, {
  icon: ShieldCheck,
  title: "Dedicated Partnership",
  highlight: "Focused Client Load",
  description: "I limit the number of clients I accept to ensure your site receives my proactive, personal attention every week.",
  agencyDrawback: "Account managers stretched thin across 15 to 20 client accounts."
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground border border-accent/20 px-3 py-1.5 text-xs font-semibold tracking-wide uppercase">
            Why Work Directly With Me
          </Badge>
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Independent SEO Consultant vs. Traditional Agency
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg leading-relaxed">
            When you partner with YE Consultancy, you work directly with me — Yassine Essaadi.
            I offer senior-level SEO expertise without the agency overhead, inflated retainers, or junior delegation.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-accent/40 hover:shadow-md flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="inline-flex items-center justify-center rounded-lg bg-primary/10 p-2.5 text-primary">
                        <Icon className="size-6" />
                      </div>
                      <span className="rounded-full bg-secondary/80 px-2.5 py-1 text-xs font-medium text-accent-foreground bg-accent/90">
                        {item.highlight}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 space-y-4">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </CardDescription>

                    <Separator className="bg-border/60" />

                    <div className="space-y-2 pt-1">
                      <div className="flex items-start gap-2 text-xs font-medium text-primary">
                        <Check className="size-4 shrink-0 mt-0.5 text-primary" />
                        <span>My Approach: Direct expertise &amp; agile execution</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <X className="size-4 shrink-0 mt-0.5 text-destructive/70" />
                        <span>Agency Reality: {item.agencyDrawback}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 rounded-2xl bg-muted/50 border border-border p-8 md:p-12 shadow-xs">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-4 max-w-2xl text-center lg:text-left">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                Personal Commitment
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                {"\"You get my personal commitment to growing your organic search strategy.\""}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                I do not pitch you senior expertise only to hand off your campaign to an intern.
                From technical audits and international strategy to AI search optimisation, I personally analyze, strategy-map, and execute every critical SEO decision for your business.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0">
              <Button asChild size="lg" className="px-6 py-3 font-medium">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 py-3 font-medium">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}